export class ReviewAutoplayController {
  constructor({
    intervalMs = 2000,
    onTick,
    setIntervalFn = globalThis.setInterval.bind(globalThis),
    clearIntervalFn = globalThis.clearInterval.bind(globalThis),
  }) {
    this.intervalMs = Math.max(2000, Number(intervalMs) || 2000);
    this.onTick = onTick;
    this.setIntervalFn = setIntervalFn;
    this.clearIntervalFn = clearIntervalFn;
    this.timer = null;
    this.state = {
      enabled: false,
      count: 0,
      userPaused: false,
      hidden: false,
      reducedMotion: false,
      staticLayout: false,
      hovered: false,
      focused: false,
      touching: false,
    };
  }

  update(patch) {
    Object.assign(this.state, patch);
    this.sync();
  }

  pause() {
    this.state.userPaused = true;
    this.sync();
  }

  play() {
    this.state.userPaused = false;
    this.sync();
  }

  canRun() {
    const state = this.state;
    return state.enabled && state.count > 1 && !state.userPaused && !state.hidden && !state.staticLayout
      && !state.reducedMotion && !state.hovered && !state.focused && !state.touching;
  }

  sync() {
    if (!this.canRun()) {
      if (this.timer !== null) this.clearIntervalFn(this.timer);
      this.timer = null;
      return;
    }
    if (this.timer !== null) return;
    this.timer = this.setIntervalFn(this.onTick, this.intervalMs);
  }

  destroy() {
    if (this.timer !== null) this.clearIntervalFn(this.timer);
    this.timer = null;
  }
}

export class LSReviewCarousel extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;

    this.track = this.querySelector('[data-ls-review-track]');
    this.slides = [...this.querySelectorAll('[data-ls-review-slide]')];
    this.dots = [...this.querySelectorAll('[data-ls-review-dot]')];
    this.previousButton = this.querySelector('[data-ls-review-previous]');
    this.nextButton = this.querySelector('[data-ls-review-next]');
    this.autoplayButton = this.querySelector('[data-ls-review-autoplay-toggle]');
    this.liveRegion = this.querySelector('[data-ls-review-live]');
    this.currentIndex = 0;
    this.touchStart = null;
    this.motionQuery = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');
    this.layoutQuery = globalThis.matchMedia?.('(min-width: 990px)');
    this.isDesktopGrid = this.layoutQuery?.matches || false;
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.controller = new ReviewAutoplayController({
      intervalMs: this.dataset.autoplayInterval,
      onTick: () => this.next({ announce: false }),
    });

    this.addEventListener('mouseenter', () => this.updateController({ hovered: true }), { signal });
    this.addEventListener('mouseleave', () => this.updateController({ hovered: false }), { signal });
    this.addEventListener('focusin', () => this.updateController({ focused: true }), { signal });
    this.addEventListener('focusout', () => {
      queueMicrotask(() => this.updateController({ focused: this.matches(':focus-within') }));
    }, { signal });
    this.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
    this.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal });
    this.addEventListener('pointercancel', (event) => this.onPointerCancel(event), { signal });
    this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    document.addEventListener('visibilitychange', () => {
      this.updateController({ hidden: document.hidden });
    }, { signal });
    this.motionQuery?.addEventListener('change', (event) => {
      this.updateController({ reducedMotion: event.matches });
    }, { signal });
    this.layoutQuery?.addEventListener('change', (event) => {
      this.isDesktopGrid = event.matches;
      this.goTo(this.currentIndex, { announce: false });
      this.updateController({ staticLayout: this.isDesktopGrid });
    }, { signal });

    this.previousButton?.addEventListener('click', () => this.previous(), { signal });
    this.nextButton?.addEventListener('click', () => this.next(), { signal });
    this.autoplayButton?.addEventListener('click', () => {
      if (this.controller.state.userPaused || this.controller.state.reducedMotion) this.play();
      else this.pause();
    }, { signal });
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goTo(index), { signal });
    });

    this.setAttribute('data-ready', '');
    this.goTo(Number(this.dataset.currentIndex) || 0, { announce: false });
    this.updateController({
      enabled: this.dataset.autoplay !== 'false',
      count: this.slides.length,
      hidden: document.hidden,
      reducedMotion: this.motionQuery?.matches || false,
      staticLayout: this.isDesktopGrid,
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.controller?.destroy();
    this.controller = null;
  }

  play() {
    if (!this.controller || this.controller.state.reducedMotion) {
      this.syncUiState();
      return;
    }
    this.controller?.play();
    this.syncUiState();
  }

  pause() {
    this.controller?.pause();
    this.syncUiState();
  }

  goTo(index, { announce = true } = {}) {
    const count = this.slides?.length || 0;
    if (!count) return;
    this.currentIndex = ((Number(index) % count) + count) % count;

    this.setAttribute('data-layout', this.isDesktopGrid ? 'grid' : 'carousel');
    this.setAttribute('aria-roledescription', document.documentElement.lang.toLowerCase().startsWith('es') ? (this.isDesktopGrid ? 'cuadrícula de opiniones' : 'carrusel') : (this.isDesktopGrid ? 'review grid' : 'carousel'));

    this.slides.forEach((slide, slideIndex) => {
      const isActive = this.isDesktopGrid || slideIndex === this.currentIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
      slide.inert = !isActive;
      this.updateSlideTabOrder(slide, isActive);
    });
    this.dots.forEach((dot, dotIndex) => {
      dot.setAttribute('aria-current', String(dotIndex === this.currentIndex));
    });
    this.dataset.currentIndex = String(this.currentIndex);
    if (this.track) this.track.style.transform = this.isDesktopGrid ? '' : `translateX(-${this.currentIndex * 100}%)`;
    if (announce && this.liveRegion) this.liveRegion.textContent = document.documentElement.lang.toLowerCase().startsWith('es') ? `Opinión ${this.currentIndex + 1} de ${count}` : `Review ${this.currentIndex + 1} of ${count}`;
  }

  next(options) {
    this.goTo((this.currentIndex || 0) + 1, options);
  }

  previous(options) {
    this.goTo((this.currentIndex || 0) - 1, options);
  }

  updateController(patch) {
    this.controller?.update(patch);
    this.syncUiState();
  }

  syncUiState() {
    if (!this.controller) return;
    const count = this.slides.length;
    const { userPaused, reducedMotion, staticLayout } = this.controller.state;
    if (reducedMotion) this.setAttribute('data-reduced-motion', 'true');
    else this.removeAttribute('data-reduced-motion');
    if (count <= 1) {
      this.dataset.autoplayState = 'static';
      this.removeAttribute('data-user-paused');
      return;
    }

    if (staticLayout) {
      this.dataset.autoplayState = 'static';
      this.removeAttribute('data-user-paused');
      if (this.autoplayButton) this.autoplayButton.setAttribute('aria-pressed', 'false');
      return;
    }

    const paused = userPaused || reducedMotion;
    this.dataset.autoplayState = paused ? 'paused' : 'playing';
    if (userPaused) this.setAttribute('data-user-paused', 'true');
    else this.removeAttribute('data-user-paused');
    if (!this.autoplayButton) return;
    this.autoplayButton.setAttribute('aria-pressed', String(!paused));
    const spanish = document.documentElement.lang.toLowerCase().startsWith('es');
    this.autoplayButton.textContent = spanish ? (paused ? 'Reproducir' : 'Pausar') : (paused ? 'Play' : 'Pause');
    this.autoplayButton.setAttribute('aria-label', spanish ? (paused ? 'Reproducir la rotación automática de opiniones' : 'Pausar la rotación automática de opiniones') : (paused ? 'Play automatic review rotation' : 'Pause automatic review rotation'));
  }

  updateSlideTabOrder(slide, isActive) {
    slide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
      if (!element.hasAttribute('data-ls-review-tabindex')) {
        element.setAttribute('data-ls-review-tabindex', element.getAttribute('tabindex') ?? '');
      }
      if (!isActive) {
        element.tabIndex = -1;
        return;
      }
      const originalTabIndex = element.getAttribute('data-ls-review-tabindex');
      if (originalTabIndex === '') element.removeAttribute('tabindex');
      else element.setAttribute('tabindex', originalTabIndex);
    });
  }

  onPointerDown(event) {
    if (event.pointerType !== 'touch') return;
    this.touchStart = { x: event.clientX, y: event.clientY };
    this.updateController({ touching: true });
  }

  onPointerUp(event) {
    if (event.pointerType !== 'touch' || !this.touchStart) return;
    const distanceX = event.clientX - this.touchStart.x;
    const distanceY = event.clientY - this.touchStart.y;
    this.touchStart = null;
    this.updateController({ touching: false });
    if (Math.abs(distanceX) < 40 || Math.abs(distanceX) <= Math.abs(distanceY)) return;
    if (distanceX < 0) this.next();
    else this.previous();
  }

  onPointerCancel(event) {
    if (event.pointerType !== 'touch') return;
    this.touchStart = null;
    this.updateController({ touching: false });
  }

  onKeydown(event) {
    if (!this.slides.length) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.goTo(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.goTo(this.slides.length - 1);
    }
  }
}

if (!customElements.get('ls-review-carousel')) {
  customElements.define('ls-review-carousel', LSReviewCarousel);
}
